import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Button,
  Divider,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store";
import { saveNotificationPreferences } from "../../features/notifications/notificationSlice";

interface NotificationSettingsProps {
  userId: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  userId,
}) => {
  const dispatch = useAppDispatch();
  const { preferences, loading } = useAppSelector(
    (state) => state.notifications
  );
  const [localSettings, setLocalSettings] = useState(
    preferences?.notificationTypes || {
      matchCreated: true,
      matchCreatedBlocked: true,
      matchBlocked: true,
      matchUnblocked: true,
      matchStarting: true,
    }
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Mettre à jour les paramètres locaux
  const handleSettingChange = (key: keyof typeof localSettings) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  // Sauvegarder les paramètres
  const handleSave = async () => {
    try {
      await dispatch(
        saveNotificationPreferences({
          userId,
          preferences: {
            notificationTypes: localSettings,
          },
        })
      ).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des paramètres:", error);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6" component="h3">
          Types de notifications
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choisissez les types de notifications que vous souhaitez recevoir, que
        ce soit par email ou par notifications push.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localSettings.matchCreated}
              onChange={() => handleSettingChange("matchCreated")}
              color="primary"
            />
          }
          label="Match créé"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mb: 2 }}
        >
          Recevoir une notification quand un match est créé
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={localSettings.matchCreatedBlocked}
              onChange={() => handleSettingChange("matchCreatedBlocked")}
              color="primary"
            />
          }
          label="Match créé bloqué"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mb: 2 }}
        >
          Recevoir une notification quand un match est créé et bloqué
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={localSettings.matchBlocked}
              onChange={() => handleSettingChange("matchBlocked")}
              color="primary"
            />
          }
          label="Match bloqué"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mb: 2 }}
        >
          Recevoir une notification quand un match est bloqué
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={localSettings.matchUnblocked}
              onChange={() => handleSettingChange("matchUnblocked")}
              color="primary"
            />
          }
          label="Match débloqué"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mb: 2 }}
        >
          Recevoir une notification quand un match est débloqué
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={localSettings.matchStarting}
              onChange={() => handleSettingChange("matchStarting")}
              color="primary"
            />
          }
          label="Match en cours"
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mb: 2 }}
        >
          Recevoir une notification quand un match commence
        </Typography>
      </Box>

      {hasChanges && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            Sauvegarder les paramètres
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default NotificationSettings;
